import { X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { FormProvider, useWvcForm } from '@/integrations/forms/FormProvider';

function AppointmentRequestFields({ onClose }: { onClose: () => void }) {
  const { register, handleSubmit, isSubmitting, isSubmitted, submitError, successMessage } = useWvcForm();

  if (isSubmitted) {
    return (
      <div className="rounded-lg bg-[#f2f7f5] p-5 text-center" role="status">
        <p className="font-semibold text-[#152238]">Request received</p>
        <p className="mt-2 text-sm text-slate-600">{successMessage}</p>
        <Button type="button" onClick={onClose} className="mt-5 rounded-lg bg-[#a32d2d] text-white hover:bg-[#8e2525]">
          Close
        </Button>
      </div>
    );
  }

  const inputClass = 'w-full !bg-[#f3f4f6] border !border-[#d1d5db] rounded-lg px-3.5 py-2.5 text-sm !text-[#1c2c47] placeholder:text-[#6b7280] focus:outline-none focus:border-[#a32d2d] focus:ring-1 focus:ring-[#a32d2d] transition-colors';

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <label htmlFor="home-appointment-name" className="mb-1.5 block text-xs font-mono uppercase tracking-wider text-[#1c2c47]">
          Full Name <span className="text-[#a32d2d]">*</span>
        </label>
        <input id="home-appointment-name" type="text" autoComplete="name" required {...register('full_name')} placeholder="Full name" className={inputClass} />
      </div>

      <div>
        <label htmlFor="home-appointment-email" className="mb-1.5 block text-xs font-mono uppercase tracking-wider text-[#1c2c47]">
          Email Address <span className="text-[#a32d2d]">*</span>
        </label>
        <input id="home-appointment-email" type="email" autoComplete="email" required {...register('email')} placeholder="name@example.com" className={inputClass} />
      </div>

      <div>
        <label htmlFor="home-appointment-phone" className="mb-1.5 block text-xs font-mono uppercase tracking-wider text-[#1c2c47]">
          Phone Number <span className="text-[#a32d2d]">*</span>
        </label>
        <input id="home-appointment-phone" type="tel" autoComplete="tel" required {...register('phone')} placeholder="(561) 000-0000" className={inputClass} />
      </div>

      <div>
        <label htmlFor="home-appointment-referral" className="mb-1.5 block text-xs font-mono uppercase tracking-wider text-[#1c2c47]">
          How did you hear about us?
        </label>
        <select id="home-appointment-referral" defaultValue="Existing Patient" {...register('referral_source')} className={`${inputClass} !text-[#1c2c47]`}>
          <option value="Existing Patient">Existing Patient</option>
          <option value="Google">Google</option>
          <option value="Word of Mouth">Word of Mouth</option>
          <option value="Social Media">Social Media</option>
          <option value="Other">Other</option>
        </select>
      </div>

      {submitError && (
        <p role="alert" className="rounded-lg bg-red-50 px-4 py-3 text-sm text-red-700">
          {submitError}
        </p>
      )}

      <div className="pt-2">
        <Button type="submit" disabled={isSubmitting} className="h-12 w-full rounded-lg bg-[#a32d2d] font-semibold uppercase tracking-[0.06em] text-white hover:bg-[#8e2525] disabled:cursor-wait disabled:opacity-60">
          {isSubmitting ? 'Sending…' : 'Submit Request'}
        </Button>
        <p className="mt-2.5 text-center text-xs text-slate-500">
          For a medical emergency, call 911. To schedule by phone, call (561) 496-7900.
        </p>
      </div>
    </form>
  );
}

export default function HomeAppointmentModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  if (!open) return null;

  return (
    <div role="dialog" aria-modal="true" aria-labelledby="home-appointment-title" className="fixed inset-0 z-50 flex items-center justify-center bg-black/75 p-4 backdrop-blur-sm" onClick={onClose}>
      <div className="relative max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-xl border border-slate-200 bg-white p-6 text-[#152238] shadow-2xl sm:p-8" onClick={(event) => event.stopPropagation()}>
        <Button type="button" variant="ghost" size="icon" onClick={onClose} className="absolute right-4 top-4 rounded-lg text-slate-400 hover:bg-slate-100 hover:text-[#152238]" aria-label="Close appointment request">
          <X className="size-5" />
        </Button>

        <div className="mb-2 flex items-center gap-2">
          <span aria-hidden="true" className="inline-block size-2 rounded-sm bg-[#a32d2d]" />
          <span className="font-mono text-xs font-semibold uppercase tracking-[0.16em] text-[#a32d2d]">Appointment Request</span>
        </div>
        <h3 id="home-appointment-title" className="mb-2 font-serif text-2xl font-semibold text-[#152238]">Schedule Your Visit</h3>
        <p className="mb-6 text-sm text-slate-600">Please enter your details below to request a consultation with our cardiology team.</p>

        <FormProvider formId="home-appointment-request">
          <AppointmentRequestFields onClose={onClose} />
        </FormProvider>
      </div>
    </div>
  );
}
